export const DISCOUNT_TYPES = [
  { id: 1, key: 'flat' },
  { id: 2, key: 'percent' }
]

/** Channel aggregation for unit limits */
export const UNITS_SOLD_SCOPES = [
  { id: 1, key: 'perLine' },
  { id: 2, key: 'perSource' }
]

/** Product aggregation for unit limits */
export const UNITS_SOLD_POOLS = [
  { id: 1, key: 'perProduct' },
  { id: 2, key: 'absolute' }
]

export const INTERVAL_DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday'
]

export function comboKey (source, method) {
  return `${source}_${method}`
}

// Same source/method column order as PriceProfileForm.sources
export const PRICE_PROFILE_SOURCES = [
  { id: 6, methods: [1] },
  { id: 7, methods: [1] },
  { id: 8, methods: [1] },
  { id: 1, methods: [1, 2, 3] },
  { id: 2, methods: [1] },
  { id: 3, methods: [1, 2] },
  { id: 4, methods: [1, 2, 3] },
  { id: 5, methods: [1, 2, 3] }
]

const COMBO_COLUMN_ORDER = PRICE_PROFILE_SOURCES.reduce((order, source) => {
  source.methods.forEach(method => {
    order.push(comboKey(source.id, method))
  })
  return order
}, [])

export function sortCombosLikePriceProfile (combos) {
  return [...(combos || [])].sort((a, b) => {
    const keyA = comboKey(a.source, a.method)
    const keyB = comboKey(b.source, b.method)
    const indexA = COMBO_COLUMN_ORDER.indexOf(keyA)
    const indexB = COMBO_COLUMN_ORDER.indexOf(keyB)
    const orderA = indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA
    const orderB = indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB
    if (orderA !== orderB) return orderA - orderB
    if (a.source !== b.source) return parseInt(a.source, 10) - parseInt(b.source, 10)
    return parseInt(a.method, 10) - parseInt(b.method, 10)
  })
}

export function combosToPayload (selected) {
  return (selected || []).map(item => ({
    source: parseInt(item.source, 10),
    method: parseInt(item.method, 10)
  }))
}

export function normalizeComboOption (combo) {
  const source = parseInt(combo.source, 10)
  const method = parseInt(combo.method, 10)
  const key = combo.key || comboKey(source, method)
  return {
    source,
    method,
    key,
    label: combo.label || key,
    source_label: combo.source_label || null,
    method_label: combo.method_label || null
  }
}

// Options coming from /source-method-options do not always cover combos already
// saved on a campaign, so selected combos are folded in to keep their labels.
export function mergeComboOptions (options, combos) {
  const merged = []
  const seen = {}
  const push = (combo) => {
    if (!combo || combo.source == null || combo.method == null) return
    const normalized = normalizeComboOption(combo)
    if (seen[normalized.key]) return
    seen[normalized.key] = true
    merged.push(normalized)
  }
  ;(options || []).forEach(push)
  ;(combos || []).forEach(push)
  return merged
}

export function matchComboOption (options, combo) {
  if (!combo) return null
  return (options || []).find(opt =>
    parseInt(opt.source, 10) === parseInt(combo.source, 10) &&
    parseInt(opt.method, 10) === parseInt(combo.method, 10)
  ) || {
    source: combo.source,
    method: combo.method,
    key: comboKey(combo.source, combo.method),
    label: combo.label || comboKey(combo.source, combo.method)
  }
}

export function resolveSelectedCombos (options, combos) {
  return (combos || []).map(combo => matchComboOption(options, combo))
}

export function getComboLabels (row) {
  if (!row) return []
  if (row.combos && row.combos.length) {
    return row.combos.map(c => c.label || comboKey(c.source, c.method))
  }
  if (row.source_method_combos && row.source_method_combos.length) {
    return row.source_method_combos.map(c => c.label || comboKey(c.source, c.method))
  }
  if (row.combos_labels) {
    return String(row.combos_labels).split(',').map(label => label.trim()).filter(Boolean)
  }
  return []
}

export function getProductLabels (row) {
  if (!row) return []
  if (row.products && row.products.length) {
    return row.products.map(p => p.name || p.label || `#${p.id}`)
  }
  if (row.products_formatted && row.products_formatted.length) {
    return row.products_formatted.map(p => p.label || p.name || `#${p.id}`)
  }
  if (row.product_ids && row.product_ids.length) {
    return row.product_ids.map(id => `#${id}`)
  }
  return []
}

export function formatTruncatedList (items, getLabel, limit) {
  const max = limit == null ? 2 : limit
  const labels = (items || [])
    .map((item, index) => {
      const label = typeof getLabel === 'function' ? getLabel(item, index) : item
      return label == null ? '' : String(label).trim()
    })
    .filter(label => label !== '')
  return {
    visible: labels.slice(0, max),
    rest: labels.slice(max),
    restCount: Math.max(labels.length - max, 0),
    full: labels
  }
}

export function formatCombosLabels (row) {
  const labels = getComboLabels(row)
  return labels.length ? labels.join(', ') : '-'
}



// WEBPACK FOOTER //
// ./src/components/Dashboard/Views/Marketing/DiscountCampaigns/discountCampaignConstants.js